import { QuestionCircleOutlined, UserOutlined, ExportOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { Layout, Menu, Dropdown } from 'antd';
import { MenuItemType } from 'antd/lib/menu/interface';
import React, { ReactNode, ReactElement, useMemo, useCallback, useState, useEffect, useRef } from 'react';

import Logo from '../../../logo.svg';

const { Sider } = Layout;

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 400;
const DEFAULT_SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_KEY = 'appSidebarWidth';

export type SidebarItemStatus = 'normal' | 'external';

export interface SidebarItem {
  icon: ReactElement;
  disabled?: boolean;
  text: string;
  href: string;
  status?: SidebarItemStatus;
}

interface AppSidebarProps {
  children?: ReactNode;
  isPublicApp?: boolean;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onWidthChange?: (width: number) => void;
  items?: SidebarItem[];
}

// Helper function to construct the legacy (old UI) URL
function getWorkbenchUrl(path: string): string {
  const hostname = window.location.hostname;

  // Extract the top-level domain (e.g., demo-owl.com from https://demo-owl.com or https://app.demo-owl.com)
  // Take the last 2 parts of the hostname (handles both demo-owl.com and app.demo-owl.com)
  const parts = hostname.split('.');
  const topDomain = parts.slice(-2).join('.');

  return `https://workbench-old.${topDomain}${path}`;
}

function AppSidebar({
  children,
  isPublicApp = false,
  collapsed = false,
  onCollapse,
  onWidthChange,
  items,
}: AppSidebarProps): JSX.Element {
  const history = useNavigate();
  const location = useLocation();

  // State for resizable width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return stored ? parseInt(stored, 10) : DEFAULT_SIDEBAR_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Notify parent of width changes
  useEffect(() => {
    onWidthChange?.(collapsed ? 80 : sidebarWidth);
  }, [sidebarWidth, collapsed, onWidthChange]);

  // Find the most specific (longest) matching path to avoid partial matches
  const selectedKey =
    items?.filter((item) => location.pathname.startsWith(item.href)).sort((a, b) => b.href.length - a.href.length)[0]?.href || '';

  const handleAccountSettings = useCallback(() => {
    void history({ to: '/users' });
  }, [history]);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || collapsed) return;

      const newWidth = e.clientX;
      if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= MAX_SIDEBAR_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, sidebarWidth, collapsed]);

  const menuItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.map((item) => {
      const status = item.status || 'normal';

      // Build label with status indicators
      let label: ReactNode = item.text;
      let style: React.CSSProperties = {};

      if (status === 'external') {
        label = (
          <span>
            {item.text} <ExportOutlined style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.8 }} />
          </span>
        );
        style = { color: '#40a9ff' }; // Blue tint for external
      }

      return {
        key: item.href,
        icon: item.icon,
        label: label,
        disabled: item.disabled,
        onClick: () => {
          void history({ to: item.href });
        },
        style: style,
      };
    });
  }, [items, history]);

  const menuKey = useMemo(() => {
    return `menu-${menuItems.map((item) => item.key).join('-')}`;
  }, [menuItems]);

  const bottomMenuItems = useMemo(() => {
    const items: MenuItemType[] = [];

    // Add workbench button if not already on workbench subdomain
    items.push({
      key: 'workbench',
      icon: <ExportOutlined />,
      label: 'Old version',
      onClick: () => {
        const searchStr = location.searchStr || '';
        const workbenchUrl = getWorkbenchUrl(location.pathname + searchStr);
        window.open(workbenchUrl, '_blank');
      },
      style: { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    });

    items.push(
      {
        key: 'help',
        icon: <QuestionCircleOutlined />,
        label: (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'user-guide',
                  label: (
                    <a target="_blank" href="https://import-io.atlassian.net/wiki/spaces/ES/overview" rel="noopener noreferrer">
                      User Guide
                    </a>
                  ),
                },
                {
                  key: 'api-docs',
                  label: (
                    <a target="_blank" href="/api/docs/" rel="noopener noreferrer">
                      API Docs
                    </a>
                  ),
                },
                {
                  key: 'version',
                  label: `v ${process.env.BUILD_NUM}`,
                  disabled: true,
                  style: { color: '#808080' },
                },
              ],
            }}
            placement="topLeft"
            trigger={['click']}
          >
            <span style={{ display: 'block', width: '100%', cursor: 'pointer' }}>Help</span>
          </Dropdown>
        ),
      },
      {
        key: 'user',
        icon: <UserOutlined />,
        label: 'User',
        onClick: handleAccountSettings,
      },
    );

    return items;
  }, [handleAccountSettings, location.pathname, location.searchStr]);

  return (
    <div ref={sidebarRef} style={{ position: 'relative' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        width={sidebarWidth}
        collapsedWidth={80}
        theme="dark"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          transition: isResizing ? 'none' : 'width 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div
            style={{
              padding: '6px 0 7px',
              textAlign: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {isPublicApp ? (
              <img src={Logo} style={{ width: '50px', filter: 'brightness(0) invert(1)' }} alt="logo" />
            ) : (
              <Link to="/orgs">
                <img src={Logo} style={{ width: '50px', filter: 'brightness(0) invert(1)' }} alt="logo" />
              </Link>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {menuItems.length > 0 ? (
              <Menu key={menuKey} theme="dark" mode="inline" selectedKeys={[selectedKey]} items={menuItems} />
            ) : (
              !collapsed && children
            )}
          </div>

          {!isPublicApp && (
            <Menu
              key="bottom-menu"
              theme="dark"
              mode="inline"
              selectable={false}
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
              items={bottomMenuItems}
            />
          )}
        </div>
      </Sider>
      {!collapsed && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'fixed',
            left: sidebarWidth - 2,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
            zIndex: 1000,
            transition: isResizing ? 'none' : 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        />
      )}
    </div>
  );
}

export default AppSidebar;
